import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ConfirmDialog, DataTable, Drawer, EmptyState, ErrorState, KpiCard, KpiReihe,
  StatusBadge, type Spalte, type Zustand,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Reservierte Korridore — Koordination VOR dem Zeichnen.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIESE SEITE IST DIE VERWALTUNG, NICHT DIE FUNKTION
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Reserviert wird auf der Belegungskarte, dort wo man den Platz SIEHT. Hier
 * stehen die Fristen, die Meldungen und der Abgleich — die Dinge, die einen
 * Überblick brauchen und keine Karte.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE FRIST IST DER GANZE MECHANISMUS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ohne Frist reserviert jeder alles, und niemand gibt etwas zurück: Eine
 * Reservierung aufzugeben kostet Arbeit und bringt dem Reservierenden nichts.
 * Nach drei Monaten ist der Hohlraum voll mit Absichten, und die Funktion ist
 * schlimmer als keine — sie zeigt Belegungen, die es nicht gibt.
 *
 * Deshalb steht die Restlaufzeit in der ersten Spalte und nicht in der
 * letzten.
 */
export function Reservierungen() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [d, setD] = useState<any | null>(null);
  const [zustand, setZustand] = useState<string>('reserviert');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [abgleich, setAbgleich] = useState<any | null>(null);
  const [arbeitet, setArbeitet] = useState(false);

  /** Die Meldungen einer Reservierung im Schubfach (Abschnitt 16). */
  const [meldungenVon, setMeldungenVon] = useState<any | null>(null);
  /** Nachfrage vor dem Freigeben — der Korridor steht danach wieder allen offen. */
  const [freigeben, setFreigeben] = useState<any | null>(null);

  const laden = useCallback(() => {
    if (!projekt) return;

    setLaeuft(true);
    setFehler(null);

    api.reservierungen(projekt.id, zustand === 'alle' ? {} : { zustand })
      .then((x: any) => setD(x))
      .catch(setFehler)
      .finally(() => setLaeuft(false));
  }, [api, projekt, zustand]);

  useEffect(() => { laden(); }, [laden]);

  const abgleichen = async () => {
    if (!projekt) return;

    setArbeitet(true);
    setFehler(null);

    try {
      setAbgleich(await api.reservierungAbgleich(projekt.id));
      laden();
    } catch (e) {
      setFehler(e);
    } finally {
      setArbeitet(false);
    }
  };

  const verlaengern = async (id: string) => {
    if (!projekt) return;

    try {
      const e: any = await api.reservierungVerlaengern(projekt.id, id);
      if (e?.hinweis) setFehler(new Error(e.hinweis));
      laden();
    } catch (e) {
      setFehler(e);
    }
  };

  const aufgeben = async (id: string) => {
    if (!projekt) return;

    try {
      await api.reservierungAufgeben(projekt.id, id);
      laden();
    } catch (e) {
      setFehler(e);
    }
  };

  const liste = useMemo(() => (d?.reservierungen ?? []) as any[], [d]);

  const spalten: Spalte<any>[] = useMemo(() => [
    {
      /* Die Restlaufzeit in der ERSTEN Spalte: Ohne Frist reserviert jeder
         alles. Was in drei Tagen abläuft, ist die Zeile, die gelesen werden
         muss — sie trägt seit U6a Zeichen und Text, nicht nur Rot. */
      schluessel: 'frist', titel: 'Frist', breite: '150px', immerSichtbar: true,
      wert: (r) => (r.tageRestlich ?? 9999),
      inhalt: (r) => {
        if (r.tageRestlich == null) {
          return new Date(r.gueltigBis).toLocaleDateString('de-DE');
        }
        const text = r.tageRestlich >= 0
          ? `${r.tageRestlich} Tage`
          : `${-r.tageRestlich} Tage über`;
        return r.tageRestlich <= 3
          ? <StatusBadge zustand="fehler" text={text} klein />
          : <span>{text}</span>;
      },
    },
    { schluessel: 'gewerk', titel: 'Gewerk', breite: '110px', wert: (r) => r.gewerk },
    { schluessel: 'zweck', titel: 'Zweck', wert: (r) => r.zweck },
    {
      schluessel: 'planer', titel: 'Planer',
      wert: (r) => r.planerName,
      inhalt: (r) => <span className="schwach">{r.planerName}</span>,
    },
    {
      schluessel: 'geschoss', titel: 'Geschoss', breite: '110px',
      wert: (r) => r.geschoss ?? '',
      inhalt: (r) => r.geschoss ?? '—',
    },
    {
      schluessel: 'hoehe', titel: 'Höhe', einheit: 'mm', ausrichtung: 'rechts',
      breite: '130px',
      wert: (r) => r.hoeheVonMm,
      inhalt: (r) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {Math.round(r.hoeheVonMm)}–{Math.round(r.hoeheBisMm)}
        </span>
      ),
    },
    {
      schluessel: 'zellen', titel: 'Zellen', ausrichtung: 'rechts', breite: '110px',
      wert: (r) => r.zellenGesamt,
      inhalt: (r) => (r.zellenGezeichnet > 0
        ? `${r.zellenGezeichnet} / ${r.zellenGesamt}`
        : r.zellenGesamt),
    },
    {
      schluessel: 'zustand', titel: 'Zustand', art: 'status', breite: '190px',
      wert: (r) => r.zustand,
      inhalt: (r) => (
        <>
          <StatusBadge zustand={zustandsart(r.zustand)} text={r.zustand} klein />
          {/* Die Zahl der Verlängerungen ist die eigentliche Kennzahl: Wer
              denselben Korridor achtmal verlängert, hat ihn nicht reserviert,
              sondern belegt. */}
          {r.verlaengerungen > 0 && (
            <span className="schwach" style={{ fontSize: 11, marginLeft: 6 }}>
              {r.verlaengerungen}× verlängert
            </span>
          )}
        </>
      ),
    },
    {
      schluessel: 'meldungen', titel: 'Meldungen', breite: '130px',
      wert: (r) => (r.meldungen ?? []).length,
      inhalt: (r) => ((r.meldungen ?? []).length > 0 ? (
        <button
          className="m-knopf"
          onClick={(e) => { e.stopPropagation(); setMeldungenVon(r); }}
        >
          <StatusBadge
            zustand="warnung" klein
            text={`${(r.meldungen ?? []).length}`}
            titel={`${(r.meldungen ?? []).length} Meldungen ansehen`}
          />
        </button>
      ) : <span className="sehr-schwach">—</span>),
    },
    {
      schluessel: 'aktionen', titel: '', breite: '180px', sortierbar: false,
      ausrichtung: 'rechts',
      inhalt: (r) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {r.zustand !== 'aufgegeben' && (
            <button className="m-knopf"
                    onClick={(e) => { e.stopPropagation(); verlaengern(r.id); }}>
              verlängern
            </button>
          )}{' '}
          {(r.zustand === 'reserviert' || r.zustand === 'verfallen') && (
            <button className="m-knopf"
                    onClick={(e) => { e.stopPropagation(); setFreigeben(r); }}>
              freigeben
            </button>
          )}
        </span>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const kopf = (
    <PageHeader
      titel="Reservierungen"
      beschreibung={'Der Lüftungsplaner weiß: hier kommt ein Hauptkanal 800×400. Er '
        + 'reserviert den Korridor, bevor er zeichnet — dann sieht der Heizungsplaner '
        + 'ihn sofort in seiner Belegung und plant nicht dagegen. Reserviert wird auf '
        + 'der Karte; hier stehen die Fristen und die Meldungen.'}
      meta={d && <>{liste.length} in dieser Auswahl</>}
      aktionen={projekt && (
        <>
          <Link to={`/projekte/${projekt.id}/koordination/belegung`}
                className="m-knopf m-knopf-haupt" style={{ textDecoration: 'none' }}>
            Auf der Karte abstecken
          </Link>
          <Link to={`/projekte/${projekt.id}/koordination`} className="m-knopf" style={{ textDecoration: 'none' }}>
            Koordination
          </Link>
        </>
      )}
    />
  );

  if (!projekt) {
    return (
      <>
        {kopf}
        <ErrorState
          klein
          titel="Kein Projekt gewählt."
          meldung="Reservierungen gehören zu einem Projekt — oben eines auswählen."
        />
      </>
    );
  }

  return (
    <>
      {kopf}

      {fehler !== null && (
        <ErrorState
          meldung="Die Reservierungen konnten nicht verarbeitet werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      )}

      {abgleich && (
        <>
          <KpiReihe>
            <KpiCard titel="Geprüft" wert={abgleich.geprueft} />
            <KpiCard titel="Gezeichnet" wert={abgleich.gezeichnet} />
            <KpiCard
              titel="Verfallen" wert={abgleich.verfallen}
              zustand={abgleich.verfallen > 0 ? 'warnung' : undefined}
              zustandText={abgleich.verfallen > 0 ? 'Frist abgelaufen' : undefined}
            />
            <KpiCard
              titel="Neue Meldungen" wert={abgleich.meldungen}
              zustand={abgleich.meldungen > 0 ? 'warnung' : undefined}
              zustandText={abgleich.meldungen > 0 ? 'ansehen' : undefined}
            />
          </KpiReihe>

          {(abgleich.hinweise ?? []).map((h: string, i: number) => (
            <p key={i} className="schwach" style={{ fontSize: 12 }}>{h}</p>
          ))}
        </>
      )}

      <DataTable
        name="Reservierungen"
        spalten={spalten}
        zeilen={liste}
        schluessel={(r) => r.id}
        laedt={laeuft}
        ausfuhr
        sortierung={{ spalte: 'frist', richtung: 'auf' }}
        werkzeuge={(
          <>
            {([['reserviert', 'gültig'], ['gezeichnet', 'gezeichnet'],
               ['verfallen', 'verfallen'], ['aufgegeben', 'aufgegeben'],
               ['alle', 'alle']] as [string, string][]).map(([k, t]) => (
              <button
                key={k}
                onClick={() => setZustand(k)}
                className={zustand === k ? 'm-knopf m-knopf-haupt' : 'm-knopf'}
                aria-pressed={zustand === k}
              >
                {t}
              </button>
            ))}

            <button className="m-knopf" onClick={abgleichen} disabled={arbeitet}>
              {arbeitet ? 'gleicht ab …' : 'Gegen den Bestand abgleichen'}
            </button>
          </>
        )}
        leer={(
          <EmptyState
            titel="Keine Reservierungen in dieser Auswahl."
            erklaerung={'Die Funktion lohnt sich erst, wenn sie VOR dem Zeichnen '
              + 'benutzt wird — hinterher ist der Korridor schon belegt.'}
            aktion={(
              <Link to={`/projekte/${projekt.id}/koordination/belegung`}
                    className="m-knopf m-knopf-haupt" style={{ textDecoration: 'none' }}>
                Auf der Karte abstecken
              </Link>
            )}
          />
        )}
      />

      <p className="schwach" style={{ fontSize: 12 }}>
        „Gezeichnet an ganz anderer Stelle" wird nicht gemeldet: Es gibt kein
        Feld am Bauteil, das es dem Zweck zuordnet — jedes Bauteil des Gewerks
        ausserhalb irgendeiner Reservierung wäre sonst ein Verstoß, und das sind
        fast alle. Geprüft ist, was in den reservierten Zellen liegt.
      </p>

      {(d?.hinweise ?? []).map((h: string, i: number) => (
        <div key={i} className="hinweis">{h}</div>
      ))}

      <Drawer
        offen={meldungenVon !== null}
        titel={meldungenVon ? `${meldungenVon.zweck} — Meldungen` : ''}
        beschreibung={meldungenVon
          ? `${(meldungenVon.meldungen ?? []).length} offene Meldungen`
          : undefined}
        beiSchliessen={() => setMeldungenVon(null)}
      >
        {(meldungenVon?.meldungen ?? []).map((m: any) => (
          <div key={m.id} style={{ marginBottom: 12 }}>
            <StatusBadge zustand="warnung" text={ARTTEXT[m.art] ?? m.art} klein />
            <div className="schwach" style={{ fontSize: 12, margin: '4px 0' }}>
              {m.text}
            </div>
            <button
              className="m-knopf"
              onClick={async () => {
                await api.reservierungMeldungGesehen(projekt.id, m.id);
                setMeldungenVon(null);
                laden();
              }}
            >
              gesehen
            </button>
          </div>
        ))}
      </Drawer>

      <ConfirmDialog
        offen={freigeben !== null}
        art="warnung"
        titel={`„${freigeben?.zweck ?? ''}" freigeben?`}
        text="Der Korridor steht dann wieder allen zur Verfügung."
        bestaetigenText="Freigeben"
        beiBestaetigen={async () => {
          const r = freigeben;
          setFreigeben(null);
          if (r) await aufgeben(r.id);
        }}
        beiAbbrechen={() => setFreigeben(null)}
      />
    </>
  );
}

/** Die Art einer Meldung im Klartext. */
const ARTTEXT: Record<string, string> = {
  abgelaufen: 'Frist um',
  nicht_gezeichnet: 'reserviert, nie gezeichnet',
  fremd_in_reservierung: 'fremdes Gewerk darin',
  ausserhalb_des_bandes: 'eigenes Bauteil ausserhalb des Bandes',
  fremde_reservierung: 'fremde Reservierung im selben Band',
  nicht_mehr_gezeichnet: 'war gezeichnet, ist es nicht mehr',
};

/**
 * Der Zustand einer Reservierung als Musterzustand.
 *
 * „verfallen" ist `warnung` und nicht `fehler`: Eine abgelaufene Reservierung
 * ist der vorgesehene Ausgang, nicht ein Schaden (Abschnitt 18).
 */
function zustandsart(zustand: string): Zustand {
  switch (zustand) {
    case 'reserviert': return 'gut';
    case 'gezeichnet': return 'info';
    case 'verfallen': return 'warnung';
    default: return 'neutral';
  }
}
